import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useSignals } from "@preact/signals-react/runtime";
import setDefaultOptions from "date-fns/esm/setDefaultOptions/index.js";
import { lazy, Suspense, useCallback, useEffect, useRef, useState, } from "react";
import { useTranslation } from "react-i18next";
import Tabs, { Tab, TabPanel } from "@ingka/tabs";
import { getWeekdayRegionOffs } from "@pdpp/lib-planta";
import { useForecastDataHandler, useForecastGraphHandler, useForecastTableHandler, } from "../../handlers";
import { countryCode$ } from "../../models/country";
import { Card } from "../../shared/components/common/card/Card";
import { selectedDriver$ } from "../../state/driverSelect";
import { selectedForecastView$ } from "../../state/forecastView";
import { selectedOrgNode$ } from "../../state/orgSelect";
import { weekStartsOn$ } from "../../state/weekStartsOn";
import { canEditForecast } from "../../utils/permissions";
import { OrgSelect } from "../orgSelect/OrgSelect";
import { Publish } from "../publish/PublishModal";
import { WorkLoadDrivers } from "../workloadDrivers/WorkLoadDrivers";
import { DownloadForecast } from "./DownloadModal";
import "./Forecast.css";
import { ForecastGraph } from "./ForecastGraph";
const FORECAST_TAB = "forecast";
const WORKLOAD_DRIVERS_TAB = "workload-drivers";
const ForecastTable = lazy(() => import("./ForecastTable").then((module) => ({
    default: module.ForecastTable,
})));
export const Forecast = ({ forecastApi, orgNode, countryCode, noPadding = false, }) => {
    useSignals();
    const [t] = useTranslation();
    const initializedRef = useRef(false);
    const mountedRef = useRef(true);
    const appliedOrgNodeRef = useRef(undefined);
    const countryCodeSignal = countryCode$.value;
    const selectedCountry = countryCode ?? countryCodeSignal;
    weekStartsOn$.value = getWeekdayRegionOffs(selectedCountry);
    setDefaultOptions({ weekStartsOn: weekStartsOn$.value });
    const [selectedUnit, setSelectedUnit] = useState(undefined);
    const [selectedDivision, setSelectedDivision] = useState(undefined);
    const [activeTab, setActiveTab] = useState(FORECAST_TAB);
    const hasInitializedRef = useRef(false);
    const prevSelectionRef = useRef({ unitId: undefined, divisionId: undefined });
    const isPublishEnabled = canEditForecast();
    const onSelectionChange = useCallback(({ countryCode: selectedCountryCode, unitId, divisionId, }) => {
        if (selectedCountryCode != null) {
            countryCode$.value = selectedCountryCode;
        }
        const unitChanged = unitId !== prevSelectionRef.current.unitId;
        const divisionChanged = divisionId !== prevSelectionRef.current.divisionId;
        const shouldClearDriver = hasInitializedRef.current &&
            prevSelectionRef.current.unitId !== undefined &&
            (unitChanged || divisionChanged);
        setSelectedUnit(unitId);
        setSelectedDivision(divisionId);
        prevSelectionRef.current = { unitId, divisionId };
        if (shouldClearDriver) {
            selectedDriver$.value = undefined;
        }
        if (!hasInitializedRef.current) {
            hasInitializedRef.current = true;
        }
    }, []);
    useEffect(() => {
        mountedRef.current = true;
        const isFirstRun = !initializedRef.current;
        const orgNodeChanged = orgNode != null &&
            orgNode !== "" &&
            orgNode !== appliedOrgNodeRef.current;
        if (isFirstRun || orgNodeChanged) {
            queueMicrotask(() => {
                if (mountedRef.current === false)
                    return;
                if (orgNode != null && orgNode !== "") {
                    appliedOrgNodeRef.current = orgNode;
                    selectedOrgNode$.value = orgNode;
                }
                if (isFirstRun) {
                    onSelectionChange({
                        countryCode: selectedCountry,
                        unitId: undefined,
                        divisionId: undefined,
                    });
                }
            });
        }
        if (isFirstRun) {
            initializedRef.current = true;
        }
        return () => {
            mountedRef.current = false;
        };
    }, [orgNode, onSelectionChange, selectedCountry]);
    const currentOrgNode = selectedOrgNode$.value;
    const currentView = selectedForecastView$.value;
    const setSelectedDriver = useCallback((value) => {
        if (typeof value === "function") {
            selectedDriver$.value = value(selectedDriver$.value);
        }
        else {
            selectedDriver$.value = value;
        }
    }, []);
    const forecastHandlerParams = {
        forecastApi,
        orgNode: currentOrgNode,
        countryCode: selectedCountry,
        unitId: selectedUnit,
        divisionId: selectedDivision,
        currentView,
        selectedDriver: selectedDriver$.value,
        setSelectedDriver,
    };
    const forecastData = useForecastDataHandler(forecastHandlerParams);
    const { driverList, driverError, refetchDrivers } = forecastData;
    useEffect(() => {
        const currentDriver = selectedDriver$.value;
        if (currentDriver == null || driverList.length === 0) {
            return;
        }
        const hasValidId = driverList.some((d) => d.id === currentDriver.id);
        if (hasValidId) {
            return;
        }
        const matchingDrivers = driverList.filter((d) => d.name === currentDriver.name);
        if (matchingDrivers.length === 0) {
            return;
        }
        const exactMatch = matchingDrivers.find((d) => d.subType === currentDriver.subType);
        const matchedDriver = exactMatch ?? matchingDrivers[0];
        if (matchedDriver) {
            selectedDriver$.value = matchedDriver;
        }
    }, [driverList]);
    const { data: tableData, tableRows, timeSlots, isLoading: tableIsLoading, driverList: tableDriverList, selectedDriver: tableSelectedDriver, view: tableView, selectedDate: tableSelectedDate, unitId: tableUnitId, forecastApi: tableForecastApi, onForecastPatched, } = useForecastTableHandler(forecastData);
    const { forecastApi: graphForecastApi, isLoading: graphIsLoading, selectedDriver: graphSelectedDriver, chartData, chartOptions, unitId, hasValidDriverName, weekStartDate, filterState, filterHandlers, filterData, accuracy, generatedAt, } = useForecastGraphHandler(forecastApi, forecastData, currentView);
    return (_jsxs("div", { className: `forecast-container ${noPadding ? "forecast-container--no-padding" : ""}`, "data-testid": "forecast-container", children: [_jsxs("div", { className: "forecast-title-container", children: [_jsx(OrgSelect, { forecastApi: forecastApi, countryCode: selectedCountry, onSelectionChange: onSelectionChange }), _jsxs("div", { className: "forecast-title-container__actions", children: [_jsx(DownloadForecast, { unitId: unitId, driverList: driverList, selectedDriver: tableSelectedDriver, forecastApi: forecastApi }), isPublishEnabled && unitId != null && (_jsx(Publish, { unitId: unitId, divisionId: selectedDivision, fetchApi: forecastApi }))] })] }), _jsx(Tabs, { className: "forecast-tabs", activeTab: activeTab, onTabChanged: setActiveTab, ariaLabel: t("forecastTab.title"), tabPanels: [
                    _jsxs(TabPanel, { tabPanelId: FORECAST_TAB, children: [_jsx(Card, { className: "forecast-graph-card", children: _jsx(ForecastGraph, { forecastApi: graphForecastApi, isLoading: graphIsLoading, driverError: driverError, onRetryDrivers: () => void refetchDrivers(), selectedDriver: graphSelectedDriver, chartData: chartData, chartOptions: chartOptions, view: currentView, unitId: unitId, hasValidDriverName: hasValidDriverName, weekStartDate: weekStartDate, filterState: filterState, filterHandlers: filterHandlers, filterData: filterData, accuracy: accuracy, generatedAt: generatedAt }) }), _jsx(Card, { className: "forecast-table-card", children: _jsx(Suspense, { fallback: null, children: _jsx(ForecastTable, { data: tableData, tableRows: tableRows, timeSlots: timeSlots, isLoading: tableIsLoading, driverList: tableDriverList, selectedDriver: tableSelectedDriver, view: tableView, selectedDate: tableSelectedDate, unitId: tableUnitId, forecastApi: tableForecastApi, onForecastPatched: onForecastPatched }) }) })] }, FORECAST_TAB),
                    _jsx(TabPanel, { tabPanelId: WORKLOAD_DRIVERS_TAB, children: _jsx(WorkLoadDrivers, { forecastApi: forecastApi, countryCode: selectedCountry, unitId: selectedUnit, divisionId: selectedDivision, isForecastSubtab: true }) }, WORKLOAD_DRIVERS_TAB),
                ], tabs: [
                    _jsx(Tab, { id: `${FORECAST_TAB}-tab`, tabPanelId: FORECAST_TAB, text: t("forecastTab.title") }, FORECAST_TAB),
                    _jsx(Tab, { id: `${WORKLOAD_DRIVERS_TAB}-tab`, tabPanelId: WORKLOAD_DRIVERS_TAB, text: t("workLoadDriversTab.label") }, WORKLOAD_DRIVERS_TAB),
                ] })] }));
};
//# sourceMappingURL=Forecast.js.map